import {
  getFirstParamValueAsSet,
  paramValuesMapToSearch,
  removeParamValue,
  searchToParamValuesMap,
  setParamValues,
  UrlSearch,
  UrlSearchProvider
} from '@tableau/data-catalog-shared';
import { KeySet } from '@tableau/data-structures';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

export function constructNewSearchForTableSelection(search: string, tableId: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const tables = paramValuesMap[CatalogUrlParamKeys.table];
  if (tables && tables.indexOf(tableId) >= 0) {
    removeParamValue(paramValuesMap, CatalogUrlParamKeys.table, tableId);
  } else {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.table, [tableId]);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructNewSearchWithClearedTableSelection(search: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const tables = paramValuesMap[CatalogUrlParamKeys.table];
  if (tables) {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.table, []);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export type DatabaseSubplaceParams = {
  readonly urlSearchValue: string;
  readonly selectedTables: KeySet;
  readonly updateTableSelection: (tableId: string) => void;
  readonly clearTableSelection: () => void;
};

export type DatabaseSubplaceParamsProviderProps = {
  readonly children: (params: DatabaseSubplaceParams) => React.ReactNode;
};

export class DatabaseSubplaceParamsProvider extends React.Component<DatabaseSubplaceParamsProviderProps> {

  public render() {
    return (
      <UrlSearchProvider>
        {urlSearch =>
          <DatabaseSubplaceParamsProviderInternal urlSearch={urlSearch}>
            {this.props.children}
          </DatabaseSubplaceParamsProviderInternal>
        }
      </UrlSearchProvider>
    );
  }
}

type DatabaseSubplaceParamsProviderInternalProps = {
  readonly urlSearch: UrlSearch;
  readonly children: (params: DatabaseSubplaceParams) => React.ReactNode;
};

class DatabaseSubplaceParamsProviderInternal extends React.Component<DatabaseSubplaceParamsProviderInternalProps> {

  // tslint:disable-next-line: typedef
  private readonly memoizedGetFirstParamValueAsSet = defaultMemoize(getFirstParamValueAsSet);

  public render() {
    return this.props.children({
      urlSearchValue: this.props.urlSearch.value,
      selectedTables: this.memoizedGetFirstParamValueAsSet(this.props.urlSearch.value, CatalogUrlParamKeys.table),
      updateTableSelection: this.updateTableSelection,
      clearTableSelection: this.clearTableSelection,
    });
  }

  private readonly clearTableSelection = () => {
    const urlSearch = this.props.urlSearch;
    const newSearchWithClearedTableSelection = constructNewSearchWithClearedTableSelection(urlSearch.value);
    urlSearch.update(newSearchWithClearedTableSelection);
  }

  private readonly updateTableSelection = (tableId: string) => {
    const urlSearch = this.props.urlSearch;
    const newSearch = constructNewSearchForTableSelection(urlSearch.value, tableId);
    urlSearch.update(newSearch);
  }

}
